import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { canEditSections } from '@/app/shell';
import { foldersQuery, sceneQuery, sectionsQuery, sensorsQuery } from '@/features/constructor/api';
import { SectionEditorPage, type EditorMode } from '@/features/section-editor/SectionEditorPage';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Section editor "Bo'lim sozlamasi" (02-constructor §6, 372:5710): `/t/:tenant/sections/:id/edit?f=:folderId&mode=3d`.
 * Requires monitoring `sections.edit` (or `dashboard.edit`); the page renders its own loading / no-access states.
 */
export const Route = createFileRoute('/_authed/t/$tenantSlug/_shell/sections/$sectionId/edit')({
  // lenient: an unknown / repeated / wrongly cased parameter is ignored instead of failing the route
  validateSearch: z.object({
    f: z.string().min(1).max(64).optional().catch(undefined),
    mode: z
      .preprocess((v) => (typeof v === 'string' ? v.toLowerCase() : v), z.enum(['3d', 'drawing']))
      .optional()
      .catch(undefined),
  }),
  loader: ({ context, params }) => {
    if (!canEditSections(context.tenant.permissions) || !UUID.test(params.sectionId)) return;
    void context.queryClient.prefetchQuery(sectionsQuery(context.tenant.id));
    void context.queryClient.prefetchQuery(foldersQuery(context.tenant.id, params.sectionId));
    void context.queryClient.prefetchQuery(sensorsQuery(context.tenant.id));
    void context.queryClient.prefetchQuery(sceneQuery(context.tenant.id, params.sectionId));
  },
  component: SectionEditRoute,
});

function SectionEditRoute() {
  const { tenant } = Route.useRouteContext();
  const { sectionId } = Route.useParams();
  const { f, mode } = Route.useSearch();
  const navigate = Route.useNavigate();
  const editorMode: EditorMode = mode === '3d' ? '3d' : 'drawing';
  return (
    <SectionEditorPage
      key={sectionId}
      tenant={tenant}
      sectionId={sectionId}
      folderId={f}
      mode={editorMode}
      onFolderChange={(folderId) => void navigate({ search: (s) => ({ ...s, f: folderId }), replace: true })}
      onModeChange={(m) => void navigate({ search: (s) => ({ ...s, mode: m === '3d' ? ('3d' as const) : undefined }), replace: true })}
    />
  );
}
